import { env } from '@/config/env';
import type { PostHog } from 'posthog-js';
import type { DEFAULT_PERIOD_OPTIONS } from '@repo/ui/components/period-selector';

import type { VaultActivityCategory } from '@/lib/vaults/activity';

export type WalletSignerRecoveryLocation =
  | 'header'
  | 'overview'
  | 'wallet-gate'
  | 'vault-detail'
  | 'composer';

export type WalletSignerRecoveryAction = 'recheck' | 'reconnect' | 'disconnect';

/** Strategist management actions on a vault they run. */
type VaultActionName =
  | 'pause'
  | 'resume'
  | 'claim-fees'
  | 'toggle-trigger'
  | 'set-trigger-venue'
  | 'set-rebalancing-params'
  | 'set-investment-limits'
  | 'set-risk-params'
  | 'trigger-rebalance';

/**
 * The storefront fields a strategist may write (v1.134.0). Mirrors
 * `ListingField` in features/vault-management/types/vault-listing.ts; the lib
 * layer cannot import from features, so the values are spelled out here.
 */
type VaultListingField =
  | 'displayName'
  | 'description'
  | 'category'
  | 'logoUrl'
  | 'highlights'
  | 'documents';

/**
 * What a finding in the strategist dashboard's attention queue is about.
 * The kind, never the vault: which vault was under its buffer is a record
 * we have no reason to keep.
 */
export type AttentionFindingKind =
  | 'paused'
  | 'idle_critical'
  | 'idle_low'
  | 'exit_partial'
  | 'exit_unknown'
  | 'pressure_high'
  | 'pressure_rising'
  | 'capacity_unmeasured'
  | 'unpriceable'
  | 'not_in_catalogue'
  | 'draft_expiring'
  | 'illiquid'
  | 'exposure_warning'
  | 'fees_truncated'
  | 'liquidity_truncated'
  | 'cash_buffer'
  | 'trigger_not_acted'
  | 'valuation_stale'
  | 'exploit_on_held_venue'
  | 'alert'
  | 'alert_unchecked';

/** The strategist dashboard's tabs, spelled here so the event stays typed. */
export type DashboardTabName = 'overview' | 'exposure' | 'fees' | 'vaults';

/**
 * The Overview's window: the shared period selector's own vocabulary, read
 * from the atom (a type only import, erased at compile time) so the event
 * cannot drift from the control that emits it.
 */
type DashboardWindowName = (typeof DEFAULT_PERIOD_OPTIONS)[number];

/**
 * The per-vault Activity tab's filter: a category a vault's own feed can
 * hold (`@/lib/vaults/activity`), or `all`.
 */
type ActivityCategoryName = 'all' | VaultActivityCategory;

type VaultActionStageName =
  | 'idle'
  | 'preparing'
  | 'signing'
  | 'confirming'
  | 'done'
  | 'error';

/**
 * How far a composer deploy got when it stopped: the deploy pipeline's own
 * stage names, spelled out here because the lib layer cannot import from
 * features.
 */
type ComposerDeployStage =
  | 'validating'
  | 'creating-intent'
  | 'confirming'
  | 'submitted'
  | 'finalising';

// PostHog product analytics behind a typed wrapper. Feature code imports ONLY
// this module — never posthog-js directly — so every event flowing out of the
// app is registered in the event map below and documented in
// docs/analytics/tracking-plan.md (update both together).
//
// Client-only: posthog-js is dynamically imported after hydration (see
// src/components/analytics-init.tsx), same pattern as src/lib/observability.ts,
// so the SDK never enters any route's first-load chunk. Without a key + app env
// (local dev, CI) analytics is disabled and every call here is a silent no-op.

/**
 * The event registry — a union of every event the app may emit, typed with the
 * properties that matter per event. An unregistered event name (or wrong
 * property shape) is a type error. Naming convention:
 * `<feature>.<object>_<past-tense-verb>`.
 *
 * Privacy: identity is the Dynamic user id only. Never wallet addresses as
 * identity or properties, never amounts/balances as user properties.
 */
interface AnalyticsEventMap {
  // App-wide wallet flow
  'app.wallet_connect_clicked': {
    location: 'wallet_gate' | 'header' | 'onboarding';
  };
  'app.wallet_connected': undefined;
  'app.wallet_session_load_timed_out': {
    location: 'wallet-gate' | 'overview';
  };

  // App-wide network switch lifecycle. Chain ids are operational context;
  // wallet addresses and wallet identifiers must never be attached.
  'app.network_switch_started': { targetChainId: number };
  'app.network_switch_succeeded': { targetChainId: number };
  'app.network_switch_rejected': { targetChainId: number };
  'app.network_switch_failed': { targetChainId: number };

  // Browser-wallet signer recovery is separate from Dynamic authentication.
  // Properties are deliberately coarse and never identify a wallet/provider.
  'app.wallet_signer_recovery_started': {
    location: WalletSignerRecoveryLocation;
    action: WalletSignerRecoveryAction;
  };
  'app.wallet_signer_recovery_succeeded': {
    location: WalletSignerRecoveryLocation;
    action: WalletSignerRecoveryAction;
  };
  'app.wallet_signer_recovery_rejected': {
    location: WalletSignerRecoveryLocation;
    action: WalletSignerRecoveryAction;
  };
  'app.wallet_signer_recovery_failed': {
    location: WalletSignerRecoveryLocation;
    action: WalletSignerRecoveryAction;
  };

  // Strategist workspace mode. Coarse values only — never wallet addresses,
  // verification evidence, or profile identifiers.
  'app.workspace_mode_switched': { mode: 'allocator' | 'strategist' };
  'app.strategist_gate_blocked': { route: 'composer' | 'vault-management' };

  // Wallet & Access opens Dynamic's manager. No account, wallet-provider, or
  // organization identifiers are attached to this interaction.
  'wallet_access.wallet_manager_opened': undefined;

  // Onboarding — authenticated wallet analysis and its evidence review. No
  // balances, amounts, holdings or wallet addresses are attached.
  'onboarding.scan_completed': undefined;
  'onboarding.analysis_reviewed': undefined;
  // Fires when DashboardLayout gives up waiting for the Dynamic SDK to
  // report sdkHasLoaded and falls through to the connect prompt instead of
  // staying stuck on the loading skeleton (see the timeout in
  // app/(dashboard)/layout.tsx). A count of these confirms whether a fix for
  // a specific stuck-load incident actually resolved it and catches the
  // failure mode recurring for a different reason later.
  'onboarding.sdk_load_timed_out': undefined;

  // Vault detail — view (fires on route open, including failed loads)
  'vault_detail.vault_viewed': { vaultId: string };

  // Vault detail — deposit lifecycle (token = asset symbol, never amounts)
  'vault_detail.deposit_started': { token: string };
  'vault_detail.deposit_confirmed': { token: string };
  'vault_detail.deposit_failed': {
    token: string;
    step: 'idle' | 'approving' | 'depositing';
  };
  'vault_detail.deposit_rejected': {
    token: string;
    step: 'idle' | 'approving' | 'depositing';
  };
  /**
   * Sent, and not confirmed in the time the app waits. Neither a failure nor
   * a decline: it may still land, and a later confirmed or failed says how.
   */
  'vault_detail.deposit_timed_out': {
    token: string;
    step: 'approving' | 'depositing';
  };

  // Vault detail — withdraw lifecycle
  'vault_detail.withdraw_started': { token: string };
  'vault_detail.withdraw_confirmed': { token: string };
  'vault_detail.withdraw_failed': {
    token: string;
    step: 'idle' | 'withdrawing';
  };
  'vault_detail.withdraw_rejected': {
    token: string;
    step: 'idle' | 'withdrawing';
  };
  /** Sent, and not confirmed in the time the app waits. Not a failure. */
  'vault_detail.withdraw_timed_out': { token: string };

  // Composer wizard — stepName mirrors WizardStepId in
  // features/composer/steps/wizard/draft.ts (the lib layer cannot import
  // from features, so the values are spelled out here; the union still
  // catches a reordered/renamed step at compile time on the caller side).
  // `flow` says which wizard: the strategist composer or the personal
  // vault builder.
  'composer.step_completed': {
    step: number;
    stepName: 'define' | 'portfolio' | 'controls' | 'backtest' | 'review';
    flow: 'public' | 'private';
  };
  'composer.deploy_confirmed': undefined;
  /**
   * `stage` is how far the deploy got (the composer's own stage names:
   * checking the configuration, registering it, the wallet prompt, sent,
   * verifying), so the funnel shows where deploys stop.
   */
  'composer.deploy_failed': {
    stage: ComposerDeployStage;
    flow: 'public' | 'private';
  };
  'composer.deploy_rejected': {
    stage: ComposerDeployStage;
    flow: 'public' | 'private';
  };
  /** Sent, and not confirmed or verified yet. Not a failure. */
  'composer.deploy_timed_out': undefined;
  /**
   * The Simulate step's automatic run against the benchmark service
   * settled: once per request, never for an answer from the client cache.
   * `refused` is the service saying it cannot price the window (no price
   * data, too few overlapping days); `failed` is everything else. The
   * window token only: no assets, weights, dates or figures.
   */
  'composer.backtest_run': {
    window: '3M' | '1Y' | '3Y' | '5Y';
    outcome: 'ok' | 'refused' | 'failed';
    flow: 'public' | 'private';
  };

  // Vault management — the strategist's dashboard. The vault contract
  // address is a public object (portfolio.vault_opened precedent) — never
  // the user's wallet.
  'vault_management.dashboard_viewed': undefined;
  'vault_management.vault_opened': { vault: string };
  'vault_management.composer_opened': {
    source: 'header' | 'empty_state' | 'deployments';
  };
  /** A Composer draft discarded from the dashboard. No ids, no addresses. */
  'vault_management.draft_discarded': { outcome: 'confirmed' | 'failed' };
  /** The strategist dashboard's own tabs — distinct from the per vault page's. */
  'vault_management.dashboard_tab_changed': { tab: DashboardTabName };
  /**
   * The Overview's window was changed on the strategist dashboard. It
   * drives the Fees generated tile, the Net flow panel and the fees chart
   * at once. The selector token only; the default on load does not emit.
   */
  'vault_management.dashboard_window_changed': { window: DashboardWindowName };
  /**
   * The shared underlying assets matrix painted with at least two vaults.
   * Once per mount; the count is bucketed so no book size travels.
   */
  'vault_management.overlap_matrix_viewed': {
    vaults: '2' | '3-5' | '6-8' | '9+';
  };
  /**
   * A finding's action was opened from the dashboard's attention queue.
   * Carries the kind of finding only — no vault, no asset, no amount.
   */
  'vault_management.attention_action_opened': { finding: AttentionFindingKind };
  /** The queue settled with nothing to raise; once per mount. */
  'vault_management.attention_quiet_shown': undefined;
  /**
   * A backend alert was marked as seen (it stays open). Fires when the
   * write settles. The code as our own name, the surface it was marked
   * from, and the outcome — no alert id, no vault, no wallet.
   */
  'vault_management.alert_acknowledged': {
    code:
      | 'paused'
      | 'cash_buffer'
      | 'trigger_not_acted'
      | 'valuation_stale'
      | 'exploit_on_held_venue'
      | 'other';
    surface: 'dashboard' | 'vault_page';
    outcome: 'confirmed' | 'failed';
  };
  /**
   * The alert destinations dialog was opened from Needs your attention.
   * How many destinations were registered (0 to 2); never which, never a
   * webhook URL, a chat, or a wallet.
   */
  'vault_management.alert_destinations_opened': { destinations: number };
  /** A Slack webhook was registered or replaced. Never the URL. */
  'vault_management.alert_destination_saved': {
    channel: 'slack';
    outcome: 'confirmed' | 'failed';
  };
  /** A one time Telegram link was asked for. Never the link or its code. */
  'vault_management.telegram_link_requested': {
    outcome: 'confirmed' | 'failed';
  };
  'vault_management.alert_destination_removed': {
    channel: 'slack' | 'telegram';
    outcome: 'confirmed' | 'failed';
  };
  /**
   * A test message was asked for. `not_delivered` is the platform refusing
   * it; `destination_off` a disabled destination (409, nothing sent);
   * `rate_limited` the one test a minute (429).
   */
  'vault_management.alert_destination_tested': {
    channel: 'slack' | 'telegram';
    outcome:
      | 'delivered'
      | 'not_delivered'
      | 'destination_off'
      | 'rate_limited'
      | 'failed';
  };
  // The strategist's per-vault page. Read-side only: the vault contract
  // address is a public object, and no wallet, amount, or investor identity
  // is ever attached.
  'vault_management.vault_page_viewed': { vault: string };
  'vault_management.vault_tab_changed': {
    tab:
      | 'overview'
      | 'performance'
      | 'investors'
      | 'risk'
      | 'strategy'
      | 'listing'
      | 'activity';
  };
  'vault_management.vault_window_changed': { window: string };
  /**
   * The Activity tab's filter. `category` is the feed's own category
   * vocabulary (`user_action`, `fee`, ...) or `all`.
   */
  'vault_management.activity_filter_changed': {
    category: ActivityCategoryName;
  };
  /**
   * "Show more" pressed on the Activity tab: how far back operators ask to
   * read. Sent on the press, so it counts requests, not loads that landed.
   */
  'vault_management.activity_more_requested': {
    category: ActivityCategoryName;
  };
  /**
   * The strategist's investor register (v1.134.0) failed to load. `reason`
   * is our own stable failure code, never a message.
   */
  'vault_management.investor_register_unavailable': { reason: string };
  /**
   * A verified strategist opened a vault that is not theirs. Deliberately
   * property-free: pairing a vault with a denied viewer is a record we have
   * no reason to keep.
   */
  'vault_management.vault_access_denied': undefined;
  /**
   * The strategist saved their vault's storefront. `fields` names which of
   * the six listing fields changed, never their values.
   */
  'vault_management.listing_saved': {
    vault: string;
    fields: VaultListingField[];
  };
  /** The save was refused or failed. `reason` is our own stable code. */
  'vault_management.listing_save_failed': { vault: string; reason: string };

  // The strategist's writes: pause (the kill switch), resume (admin only,
  // kept for that surface) and the fee claim. `vault` is the contract
  // address (a public object); the wallet that signs is never attached, even
  // though every one of these events is about a specific wallet's authority,
  // and a claim's amounts are never attached either.
  // `stage` mirrors VaultActionStage in
  // features/vault-management/hooks/use-vault-action.ts (the lib layer cannot
  // import from features, so the values are spelled out here).
  'vault_management.vault_action_opened': {
    vault: string;
    action: VaultActionName;
  };
  'vault_management.vault_action_started': {
    vault: string;
    action: VaultActionName;
  };
  'vault_management.vault_action_confirmed': {
    vault: string;
    action: VaultActionName;
  };
  /** Declined in the wallet. Flow abandonment, deliberately not a failure. */
  'vault_management.vault_action_rejected': {
    vault: string;
    action: VaultActionName;
    stage: VaultActionStageName;
  };
  'vault_management.vault_action_failed': {
    vault: string;
    action: VaultActionName;
    stage: VaultActionStageName;
    /** Our own stable failure code, never a revert message or a raw error. */
    reason: string;
  };
  /**
   * Sent, and not confirmed in the time the page waits. Neither a failure
   * nor a decline: it may still land, and a later confirmed or failed says
   * how.
   */
  'vault_management.vault_action_timed_out': {
    vault: string;
    action: VaultActionName;
  };
  /**
   * The control was offered but the chain says this wallet cannot use it.
   * Fires once per action and reason per page visit: how often strategists
   * reach for a control they cannot use is the signal for whether the role
   * model we built the page on matches the one the contracts enforce.
   */
  'vault_management.vault_action_blocked': {
    vault: string;
    action: VaultActionName;
    reason: string;
  };
  /**
   * The strategist opened where a change still out for the vault stands,
   * from the line under the header's controls: how often a vault is held
   * by a transaction that has not settled, or a wallet request nobody
   * answered, and at which point. `hold` mirrors `VaultActionHold['kind']`
   * in features/vault-management/utils/vault-action-hold.ts.
   */
  'vault_management.held_action_viewed': {
    vault: string;
    action: VaultActionName;
    hold: 'confirming' | 'slow' | 'asking' | 'preparing';
  };
  /**
   * The strategist pressed "Check if a rebalance would trigger now" and the
   * check settled. Measures whether the check is used, and how often the
   * per strategist budget or the chain read refuses it.
   */
  'vault_management.rebalance_check_completed': {
    vault: string;
    outcome:
      | 'would_fire'
      | 'would_not_fire'
      | 'rate_limited'
      | 'unavailable'
      | 'not_listed'
      | 'failed';
  };

  // Portfolio — live analytics surfaces
  'portfolio.period_changed': { period: string };
  'portfolio.lens_changed': {
    section: 'owned-positions' | 'look-through';
    lens: string;
  };
  'portfolio.benchmark_changed': { benchmark: string; enabled: boolean };
  'portfolio.methodology_opened': undefined;
  'vault_detail.window_changed': { window: string };
  'vault_detail.benchmark_changed': { benchmark: string };
  'portfolio.positions_sorted': {
    field: 'position' | 'value' | 'weight';
    direction: 'asc' | 'desc';
  };
  /** Vault contract address — a public object, never the user's wallet. */
  'portfolio.vault_opened': { vault: string | null };

  // Risk & liquidity — combined analytics destination
  'risk_liquidity.view_changed': { view: string };
  // Index symbol only (e.g. SPX) — never values, weights, or addresses.
  'risk_liquidity.benchmark_changed': { benchmark: string };
  // Coarse UI choices only — never member keys, weights, or addresses.
  'risk_liquidity.contribution_lens_changed': { lens: string };
  'risk_liquidity.contribution_measure_changed': { measure: string };

  // Overview — derived portfolio health
  'overview.health_methodology_opened': undefined;
  'overview.health_category_opened': { category: string };
  'overview.improvement_actioned': { improvement: string };

  // Activity — typed transaction ledger. Coarse UI choices only: never
  // amounts, tokens, addresses, or record contents.
  'activity.period_changed': { period: string };
  'activity.filter_changed': { kind: string };
  'activity.ledger_exported': undefined;
  // The reader left older records for the newest ones ("Back to latest").
  'activity.latest_shown': undefined;
}

type AnalyticsEvent = keyof AnalyticsEventMap;

let client: PostHog | null = null;
let initialized = false;

// Queue-before-init: events tracked between first render and the dynamic SDK
// import must not throw or vanish — they buffer here (bounded) and flush once
// init completes. When analytics is disabled they are dropped at call time.
const MAX_QUEUE = 50;
const queue: Array<[AnalyticsEvent, Record<string, unknown> | undefined]> = [];
let pendingDistinctId: string | null = null;

function isEnabled(): boolean {
  return Boolean(env.NEXT_PUBLIC_POSTHOG_KEY && env.NEXT_PUBLIC_APP_ENV);
}

/**
 * Initializes PostHog. No-ops unless the PostHog key and app env are both
 * present (i.e. on Render staging/production) — local dev and CI run
 * analytics-free.
 */
export async function initAnalytics(): Promise<void> {
  if (initialized) return;
  const key = env.NEXT_PUBLIC_POSTHOG_KEY;
  const appEnv = env.NEXT_PUBLIC_APP_ENV;
  if (!key || !appEnv) return;

  initialized = true;

  let posthog: PostHog;
  try {
    ({ default: posthog } = await import('posthog-js'));
  } catch (error) {
    // Failed SDK load (flaky network, deploy skew): allow a later call to
    // retry instead of wedging with events buffered forever.
    initialized = false;
    throw error;
  }

  posthog.init(key, {
    api_host: env.NEXT_PUBLIC_POSTHOG_HOST,
    // App Router SPA navigations don't reload the page — 'history_change'
    // captures a $pageview on pushState/replaceState/popstate (PostHog's
    // recommended Next.js App Router pattern), plus the initial load.
    capture_pageview: 'history_change',
    // We run a deliberate tracking plan (docs/analytics/tracking-plan.md);
    // autocapture would add noisy, unreviewed events with no privacy vetting.
    autocapture: false,
    // Datadog RUM already records session replays — recording twice doubles
    // cost and privacy surface for zero insight.
    disable_session_recording: true,
    respect_dnt: true,
    // Only create person profiles for identified (logged-in) users; anonymous
    // browsing stays event-only.
    person_profiles: 'identified_only'
  });

  client = posthog;

  // Super property on every event, including the buffered ones flushed
  // below. Without it staging and production traffic is indistinguishable
  // once both report into the same PostHog project — APP_ENV would only be
  // gating whether analytics runs, never saying which environment an event
  // came from, so every funnel would quietly blend test traffic with real
  // users. Registered before the flush so queued events carry it too.
  posthog.register({ environment: appEnv });

  // Identity first, then the buffered events, so they attribute correctly.
  if (pendingDistinctId) {
    client.identify(pendingDistinctId);
    pendingDistinctId = null;
  }
  for (const [event, properties] of queue.splice(0)) {
    client.capture(event, properties);
  }
}

/**
 * Tracks a registered analytics event. Safe to call anywhere, any time:
 * disabled → dropped silently; enabled but SDK not loaded yet → buffered.
 */
export function track<E extends AnalyticsEvent>(
  event: E,
  ...args: AnalyticsEventMap[E] extends undefined
    ? [properties?: undefined]
    : [properties: AnalyticsEventMap[E]]
): void {
  if (!isEnabled()) return;
  const properties = args[0] as Record<string, unknown> | undefined;
  if (client) {
    client.capture(event, properties);
    return;
  }
  if (queue.length < MAX_QUEUE) queue.push([event, properties]);
}

/**
 * Ties the session to the Dynamic user id — the ONLY permitted identity.
 * Wallet addresses must never be used as identity (they are public,
 * cross-referenceable identifiers; the Dynamic id is pseudonymous and ours).
 */
export function identify(dynamicUserId: string): void {
  if (!isEnabled() || !dynamicUserId) return;
  if (client) {
    client.identify(dynamicUserId);
    return;
  }
  pendingDistinctId = dynamicUserId;
}

/** Clears the identified user on logout (back to an anonymous session). */
export function reset(): void {
  if (!isEnabled()) return;
  pendingDistinctId = null;
  client?.reset();
}
